<template>
  <div class="auth-page">
    <UcariHeader />
    <main class="auth-shell">
      <aside class="auth-story">
        <span class="eyebrow">Welcome back</span>
        <h1>Your wholesale portal, built for easy reordering.</h1>
        <p>Access live partner pricing, place secure orders, and review your order history in one place.</p>
        <ul>
          <li><span uk-icon="icon: check"></span> Live wholesale product catalog</li>
          <li><span uk-icon="icon: check"></span> Secure saved addresses and cards</li>
          <li><span uk-icon="icon: check"></span> Complete order history</li>
        </ul>
      </aside>
      <form @submit.prevent="login" class="auth-card login-user">
        <div class="auth-card__heading">
          <span class="eyebrow">Partner portal</span>
          <h2>Sign in to your account</h2>
          <p>Use the email and password associated with your UCARI partner account.</p>
        </div>
        <div v-if="message" class="uk-alert-danger" role="alert">{{ message }}</div>
        <div>
          <div class="uk-margin">
            <label for="email">Email</label>
            <input id="email" class="uk-input" autocomplete="email"
                   :class="{ 'error-border': (isEmailInvalid || !email) && (emailTouched || formSubmitted) }" 
                   type="email" 
                   v-model="email" 
                   @blur="validateEmail" 
                   @focus="emailTouched = true" 
                   />
            <div class="error" v-if="emailMessage">{{ emailMessage }}</div>
          </div>

          <div class="uk-margin">
            <label for="password">Password</label>
            <div class="uk-inline">
              <input id="password" :type="showPassword ? 'text' : 'password'" autocomplete="current-password"
                     class="uk-input" 
                     :class="{ 'error-border': (isPasswordInvalid || !password) && (passwordTouched || formSubmitted) }" 
                     v-model="password" 
                     @input="handlePasswordInput" 
                     @focus="passwordTouched = true" 
                     />
              <button type="button" class="password-toggle" @click="togglePasswordVisibility">{{ showPassword ? 'Hide' : 'Show' }}</button>
            </div>
          </div>

          <div class="uk-margin">
            <label class="terms-check">
              <input class="uk-checkbox uk-margin-small-right" type="checkbox" v-model="ackAccepted" />
              <span>
                I agree to UCARI’s
                <router-link :to="{ name: 'RetailTerms' }">Retail Sales &amp; Pricing Acknowledgment</router-link>.
              </span>
            </label>
          </div>

          <button
            class="uk-button uk-button-primary uk-width-1-1 login-button"
            type="submit"
            :disabled="isFormInvalid || loading || !ackAccepted"
          >
            <span v-if="!loading">Sign in</span>
            <span v-else>
              Signing in
              <span class="uk-margin-small-left" uk-spinner="ratio: 0.6"></span>
            </span>
          </button>
          <div class="auth-card__links">
            <span>New to UCARI? <router-link to="/register">Create an account</router-link></span>
            <router-link to="/request-password-reset">Forgot password?</router-link>
          </div>
        </div>
      </form>
    </main>
    <RetailerFooter />
  </div>
</template>

<script>
import axios from 'axios';
import UcariHeader from './components/UcariHeader.vue';
import RetailerFooter from './components/RetailerFooter.vue';

export default {
  created() {
    document.title = 'UCARI Partner Portal Sign In | UCARI';
    const requestedRedirect = this.$route.query.redirect;
    this.redirectUrl = typeof requestedRedirect === 'string' && requestedRedirect.startsWith('/')
      ? requestedRedirect
      : '/shop';

    localStorage.removeItem('accessToken');
    localStorage.removeItem('cid');
    localStorage.removeItem('squareCustomerId');
    localStorage.removeItem('userEmail');
  },  
  components: {
    UcariHeader,
    RetailerFooter,
  },
  data() {
    return {
      email: '',
      password: '',
      message: '',
      isEmailInvalid: false,
      isPasswordInvalid: false,
      showPassword: false,
      emailTouched: false,
      passwordTouched: false,
      formSubmitted: false,
      loading: false,
      redirectUrl: '',
      emailMessage: '',
      ackAccepted: localStorage.getItem('ucari_ack_accepted') === '1',
    };
  },
  computed: {
    isFormInvalid() {
      return this.isEmailInvalid || this.isPasswordInvalid || !this.email || !this.password;
    }
  },
  watch: {
    ackAccepted(val) {
      localStorage.setItem('ucari_ack_accepted', val ? '1' : '0');
    }
  },  
  methods: {
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    validateEmail() {
      const re = /\S+@\S+\.\S+/;
      this.isEmailInvalid = !re.test(this.email);
      if (this.isEmailInvalid) {
        this.emailMessage = 'Invalid email address';
      } else {
        this.emailMessage = '';
      }
    },
    handlePasswordInput() {
      this.isPasswordInvalid = !this.password;
    },
    async login() {
      if (!this.ackAccepted) return;

      this.formSubmitted = true;
      this.validateEmail();
      this.handlePasswordInput();

      if (this.isFormInvalid) {
        this.message = 'Please correct the errors in the form';
        return;
      }

      this.loading = true;

      try {
        const base = process.env.VUE_APP_API_URL || '';
        const url = `${base}/api/retailer/login`;
        const response = await axios.post(url, {
          email: this.email,
          password: this.password
        });
        /*
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/retailer/login?redirect=${encodeURIComponent(this.redirectUrl)}`, {
          email: this.email,
          password: this.password
        });
        */

        if (response.status === 200) {
          const token = response.data.token;
          const cid = response.data.cid;
          const squareCustomerId = response.data.squareCustomerId;
          if (token) {
            localStorage.setItem('accessToken', token);
            localStorage.setItem('cid', cid);
            localStorage.setItem('squareCustomerId', squareCustomerId);
            localStorage.setItem('userEmail', this.email);
            axios.defaults.headers.common.Authorization = `Bearer ${token}`;
            axios.defaults.headers.common.cid = cid;
          }
          this.$router.push(this.redirectUrl);
        } else if (response.status === 400) {
          this.message = response.data.error;
        } else if (response.status === 500) {
          this.message = response.data.error;
        } else {
          this.message = response.data.message || 'Sign in failed';
        }
      } catch (error) {
        this.message = (error.response?.data?.error || error.message);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
